import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Search, Clock, X, Package, Building2, FileText, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSmartSearch } from "@/hooks/useSmartSearch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface SearchResult {
  id: string;
  type: 'product' | 'supplier' | 'rfq';
  title: string;
  subtitle?: string;
  url: string;
}

interface LiveSearchResults {
  products: SearchResult[];
  suppliers: SearchResult[];
  rfqs: SearchResult[];
}

interface CompactSearchBarProps {
  placeholder?: string;
  className?: string;
}

export function CompactSearchBar({
  placeholder = "Search products, suppliers, RFQs...",
  className,
}: CompactSearchBarProps) {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [liveResults, setLiveResults] = useState<LiveSearchResults | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { recentSearches, trackSearch, clearRecentSearches } = useSmartSearch();

  // Debounced live search (Issue #12 fix)
  const performLiveSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setLiveResults(null);
      return;
    }

    setIsSearching(true);
    try {
      const searchTerm = `%${searchQuery}%`;

      // Search products, suppliers, and RFQs in parallel
      const [productsRes, suppliersRes, rfqsRes] = await Promise.all([
        supabase
          .from('products')
          .select('id, name, categories(name)')
          .or(`name.ilike.${searchTerm},sku.ilike.${searchTerm}`)
          .eq('status', 'active')
          .limit(3),
        supabase
          .from('profiles')
          .select('id, company_name, city, state')
          .ilike('company_name', searchTerm)
          .limit(3),
        supabase
          .from('rfqs')
          .select('id, title, product_name')
          .or(`title.ilike.${searchTerm},product_name.ilike.${searchTerm}`)
          .in('status', ['open', 'pending', 'active'])
          .limit(3),
      ]);

      const products: SearchResult[] = (productsRes.data || []).map((p) => ({
        id: p.id,
        type: 'product' as const,
        title: p.name,
        subtitle: (p.categories as { name?: string } | null)?.name,
        url: `/product/${p.id}`,
      }));

      const suppliers: SearchResult[] = (suppliersRes.data || []).map((s) => ({
        id: s.id,
        type: 'supplier' as const,
        title: s.company_name || 'Unknown Supplier',
        subtitle: [s.city, s.state].filter(Boolean).join(', '),
        url: `/supplier/${s.id}`,
      }));

      const rfqs: SearchResult[] = (rfqsRes.data || []).map((r) => ({
        id: r.id,
        type: 'rfq' as const,
        title: r.title,
        subtitle: r.product_name || undefined,
        url: `/rfq/${r.id}`,
      }));

      setLiveResults({ products, suppliers, rfqs });
    } catch (error) {
      logger.error('Live search error', { error });
      setLiveResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce effect for live search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (query.length >= 2) {
        performLiveSearch(query);
      } else {
        setLiveResults(null);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [query, performLiveSearch]);

  // Detect current portal from pathname (Issue #33 fix - search scope)
  const getCurrentPortal = (): { name: string; route: string } => {
    const pathname = location.pathname;

    if (pathname.startsWith('/admin')) {
      return { name: 'Admin', route: '/admin/search' };
    } else if (pathname.startsWith('/supplier')) {
      return { name: 'Supplier', route: '/supplier/search' };
    } else if (pathname.startsWith('/buyer')) {
      return { name: 'Buyer', route: '/buyer/search' };
    }

    // Default fallback for non-portal pages
    return { name: 'All', route: user ? '/buyer/search' : '/auth' };
  };

  const currentPortal = getCurrentPortal();

  const getSearchRoute = (searchQuery: string): string => {
    if (currentPortal.route === '/auth') {
      return `/auth?redirect=/buyer/search?q=${encodeURIComponent(searchQuery)}`;
    }
    return `${currentPortal.route}?q=${encodeURIComponent(searchQuery)}`;
  };

  const handleSearch = () => {
    if (!query.trim()) return;
    
    trackSearch(query);
    const route = getSearchRoute(query);
    navigate(route);
    setIsFocused(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    trackSearch(suggestion);
    const route = getSearchRoute(suggestion);
    navigate(route);
    setIsFocused(false);
  };

  const hasRecentSearches = recentSearches && recentSearches.length > 0;
  const hasLiveResults = liveResults && (liveResults.products.length > 0 || liveResults.suppliers.length > 0 || liveResults.rfqs.length > 0);
  const hasSuggestions = hasRecentSearches || hasLiveResults || isSearching;

  // Keyboard navigation
  const { selectedIndex, handleKeyDown, reset } = useKeyboardNavigation({
    itemCount: recentSearches?.length || 0,
    enabled: isFocused && hasSuggestions,
    onSelect: (index) => {
      if (recentSearches && recentSearches[index]) {
        handleSuggestionClick(recentSearches[index]);
      }
    },
  });

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={isFocused && hasSuggestions} onOpenChange={setIsFocused}>
        <PopoverTrigger asChild>
          <div className="flex w-full min-w-0 items-center overflow-hidden rounded-xl border border-border bg-white">
            <span className="shrink-0 pl-4 text-muted-foreground" aria-hidden>
              <Search className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
            <Input
              type="search"
              data-search-input
              aria-label="Search products, suppliers, and RFQs"
              placeholder={placeholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                reset(); // Reset keyboard navigation when typing
              }}
              onKeyDown={(e) => {
                // Handle keyboard navigation first
                if (isFocused && hasSuggestions && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter")) {
                  handleKeyDown(e);
                  return;
                }

                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              onFocus={() => {
                setIsFocused(true);
                reset();
              }}
              onBlur={(e) => {
                // Delay blur to allow click events on suggestions
                setTimeout(() => {
                  if (e.currentTarget && !e.currentTarget.contains(document.activeElement)) {
                    setIsFocused(false);
                    reset();
                  }
                }, 150);
              }}
              className="h-11 w-full min-w-0 rounded-none border-0 bg-transparent px-2 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            </div>
            {query && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setQuery("");
                  setIsFocused(true);
                }}
              >
                <X className="size-3" />
              </Button>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="z-dropdown w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-white p-0 shadow-lg sm:max-w-md"
          align="start"
          side="bottom"
          sideOffset={4}
          alignOffset={0}
          collisionPadding={16}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={() => setIsFocused(false)}
        >
          <div className="max-h-80 overflow-y-auto">
            {/* Live Search Results (Issue #12 fix) */}
            {isSearching && (
              <div className="p-4 text-center">
                <Loader2 className="mx-auto size-4 animate-spin text-muted-foreground" />
                <p className="mt-1 text-xs text-muted-foreground">Searching...</p>
              </div>
            )}

            {!isSearching && hasLiveResults && (
              <>
                {/* Products Section */}
                {liveResults.products.length > 0 && (
                  <div className="border-b border-border p-2">
                    <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <Package className="size-3" />
                      Products
                    </div>
                    <div className="space-y-1">
                      {liveResults.products.map((result) => (
                        <button
                          key={result.id}
                          onClick={() => {
                            navigate(result.url);
                            setIsFocused(false);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          className="w-full rounded-xl px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted/60 focus:bg-muted/60 focus:outline-none"
                        >
                          <div className="truncate font-medium text-foreground">{result.title}</div>
                          {result.subtitle && (
                            <div className="truncate text-muted-foreground">{result.subtitle}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suppliers Section */}
                {liveResults.suppliers.length > 0 && (
                  <div className="border-b border-border p-2">
                    <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <Building2 className="size-3" />
                      Suppliers
                    </div>
                    <div className="space-y-1">
                      {liveResults.suppliers.map((result) => (
                        <button
                          key={result.id}
                          onClick={() => {
                            navigate(result.url);
                            setIsFocused(false);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          className="w-full rounded-xl px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted/60 focus:bg-muted/60 focus:outline-none"
                        >
                          <div className="truncate font-medium text-foreground">{result.title}</div>
                          {result.subtitle && (
                            <div className="truncate text-muted-foreground">{result.subtitle}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* RFQs Section */}
                {liveResults.rfqs.length > 0 && (
                  <div className="border-b border-border p-2">
                    <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <FileText className="size-3" />
                      RFQs
                    </div>
                    <div className="space-y-1">
                      {liveResults.rfqs.map((result) => (
                        <button
                          key={result.id}
                          onClick={() => {
                            navigate(result.url);
                            setIsFocused(false);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          className="w-full rounded-xl px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted/60 focus:bg-muted/60 focus:outline-none"
                        >
                          <div className="truncate font-medium text-foreground">{result.title}</div>
                          {result.subtitle && (
                            <div className="truncate text-muted-foreground">{result.subtitle}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* View All Results Link */}
                <div className="p-2">
                  <button
                    onClick={handleSearch}
                    onMouseDown={(e) => e.preventDefault()}
                    className="w-full py-1 text-center text-xs text-foreground hover:underline"
                  >
                    View all results for "{query}"
                  </button>
                </div>
              </>
            )}

            {/* No Results */}
            {!isSearching && query.length >= 2 && !hasLiveResults && (
              <div className="p-4 text-center">
                <p className="text-xs text-muted-foreground">No results found for "{query}"</p>
                <button
                  onClick={handleSearch}
                  onMouseDown={(e) => e.preventDefault()}
                  className="mt-1 text-xs text-foreground hover:underline"
                >
                  Search anyway
                </button>
              </div>
            )}

            {/* Recent Searches (show when no live results and query is short) */}
            {!isSearching && !hasLiveResults && query.length < 2 && hasRecentSearches && (
              <div className="border-b border-border p-2">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <Clock className="size-3" />
                    Recent
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearRecentSearches();
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <div className="space-y-1">
                  {recentSearches.map((search, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(search)}
                      onMouseDown={(e) => {
                        // Prevent input blur before click
                        e.preventDefault();
                      }}
                      className={cn(
                        "w-full text-left px-2 py-1.5 text-xs rounded-xl transition-colors focus:outline-none break-words min-w-0",
                        selectedIndex === index
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted/60 focus:bg-muted/60 text-foreground"
                      )}
                    >
                      <span className="block truncate" title={search}>{search}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

